import { createSelector } from '@reduxjs/toolkit'
import getFullName from '@ancon/wildcat-utils/user/getFullName'
import formatPhoneNumber from '@ancon/wildcat-utils/phoneNumber/formatPhoneNumber'

import type { RootState } from '../../../store/types'
import { ClientContextState } from '../types'
import hasIncompleteCustomerDetails from '../utils/hasIncompleteCustomerDetails'

import clientContextCompanyUsersEntityAdapter from './clientContextCompanyUsersEntityAdapter'

function clientContextStateSelector<K extends keyof ClientContextState>(
  state: RootState,
  key: K,
) {
  return state.clientContext[key]
}

// Adapter selectors
const companyUsersAdapterSelectors =
  clientContextCompanyUsersEntityAdapter.getSelectors(
    (state: RootState) => state.clientContext.companyUsers,
  )

export const {
  selectById: clientContextCompanyUserByIdSelector,
  selectAll: clientContextCompanyUsersListSelector,
  selectTotal: clientContextTotalCompanyUsersSelector,
} = companyUsersAdapterSelectors

// Client context selectors
export const clientContextSelector = (state: RootState) =>
  clientContextStateSelector(state, 'clientContext')

export const clientContextFetchPendingSelector = (state: RootState) =>
  clientContextStateSelector(state, 'clientContextFetchPending')

export const clientContextCustomerSelector = (state: RootState) =>
  clientContextSelector(state)?.customer

export const clientContextCustomerIdSelector = (state: RootState) =>
  clientContextCustomerSelector(state)?.customerUserId

export const clientContextCustomerPhoneNumberSelector = (state: RootState) =>
  clientContextCustomerSelector(state)?.telephoneNumber

export const clientContextCustomerFirstNameSelector = (state: RootState) =>
  clientContextCustomerSelector(state)?.firstName

// Customer details selectors
export const clientContextCustomerDetailsSelector = (state: RootState) =>
  clientContextStateSelector(state, 'customer')

export const clientContextCurrentCompanyUserIdSelector = (state: RootState) =>
  clientContextStateSelector(state, 'currentCompanyUserId')

export const clientContextCustomerEmailSelector = (state: RootState) =>
  clientContextCustomerDetailsSelector(state)?.email

export const clientContextCustomerEmailHashSelector = (state: RootState) =>
  clientContextCustomerDetailsSelector(state)?.emailHash

export const clientContextCustomerNameSelector = createSelector(
  [
    clientContextCustomerSelector,
    (state: RootState, fallback?: string) => fallback, // Default to "N/A"
  ],
  (clientContextCustomer, fallback) =>
    getFullName(clientContextCustomer, fallback),
)

export const clientContextAuthenticationUserSelector = (state: RootState) =>
  clientContextStateSelector(state, 'clientContext')?.authenticationUser

export const clientContextCurrentCompanyUserSelector = createSelector(
  [clientContextCurrentCompanyUserIdSelector, state => state],
  (companyId, state) =>
    clientContextCompanyUserByIdSelector(state, companyId ?? ''),
)

export const clientContextCurrentUserCompanyNameSelector = createSelector(
  [clientContextCurrentCompanyUserSelector],
  currentCompanyUser => currentCompanyUser?.companyName ?? '',
)

export const clientContextCurrentUserCompanyIdSelector = createSelector(
  [clientContextCurrentCompanyUserSelector],
  currentCompanyUser => currentCompanyUser?.companyId ?? null,
)

export const clientContextHasCompanyProfilesSelector = createSelector(
  [clientContextSelector, clientContextTotalCompanyUsersSelector],
  (clientContext, totalCompanyUsers) =>
    !!clientContext?.isCompany && totalCompanyUsers > 0,
)

export const clientContextIsCompanyUserSelector = createSelector(
  [clientContextCurrentCompanyUserIdSelector],
  currentCompanyUserId => !!currentCompanyUserId,
)

export const clientContextCustomerAddressesSelector = (state: RootState) =>
  clientContextCustomerDetailsSelector(state)?.addresses

export const currentCompanyUserDefaultInvoiceReferenceNoteSelector =
  createSelector(
    [clientContextCurrentCompanyUserSelector],
    companyUser => companyUser?.defaultInvoiceReferenceNote || '',
  )

export const currentCompanyUserOverrideMenuTreeSelector = createSelector(
  [clientContextCurrentCompanyUserSelector],
  companyUser => companyUser?.overrideMenuTreeId || null,
)

export const clientContextProfileNameSelector = createSelector(
  [
    clientContextIsCompanyUserSelector,
    clientContextCurrentUserCompanyNameSelector,
    clientContextCustomerSelector,
  ],
  (isCompanyUser, currentUserCompanyName, customer) => {
    if (isCompanyUser) {
      return currentUserCompanyName
    }

    const customerName = getFullName(customer, '')

    if (customerName) {
      return customerName
    }

    if (customer?.telephoneNumber) {
      return formatPhoneNumber(customer?.telephoneNumber)
    }

    return null
  },
)

export const clientContextAuthenticationUserSignInProviderSelector =
  createSelector(
    clientContextAuthenticationUserSelector,
    authenticationUser => authenticationUser?.signInProvider,
  )

export const clientContextIsAnonymousCustomerSelector = createSelector(
  clientContextAuthenticationUserSelector,
  authenticationUser =>
    !!authenticationUser?.signInProvider &&
    authenticationUser?.signInProvider === 'anonymous',
)

export const clientContextIsCustomerWithIncompleteDetailsSelector =
  createSelector(clientContextSelector, clientContext => {
    if (clientContext) {
      const { authenticationUser, customer } = clientContext

      return (
        !!authenticationUser?.signInProvider &&
        authenticationUser.signInProvider !== 'anonymous' &&
        hasIncompleteCustomerDetails(customer)
      )
    }

    return false
  })

export const clientContextCustomerLastUpdatedDateSelector = createSelector(
  [clientContextCustomerDetailsSelector],
  customer => customer?.modified || customer?.created,
)

export const clientContextCompanyMemberIdByCompanyIdSelector = createSelector(
  [clientContextCompanyUsersListSelector, (state, companyId) => companyId],
  (companyUsers, companyId) =>
    companyUsers.find(user => user.companyId === companyId)?.id,
)

export const clientContextCompanyIdByCompanyMemberIdSelector = createSelector(
  [clientContextCompanyUserByIdSelector],
  companyUser => companyUser?.companyId ?? null,
)
